import {
  useLatestScanResultsByNeedId,
  useScanIdsByNeedId,
  useOrgScanUsage,
} from '@/utils/hooks/scans'
import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import LoginSignup from '@/components/LoginSignup'
import ErrorMessage from '@/components/ErrorMessage'
import {
  getDraftRestriction,
  isFreeTier,
  limitResultsForFreeTier,
  hasFreeUserReachedScanLimit,
} from '@/utils/utils'
import UpgradeCTA from '@/components/UpgradeCTA'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { isValid, differenceInDays } from 'date-fns'
const accountContactEmail = import.meta.env.VITE_ACCOUNT_CONTACT_EMAIL
const noticeUntil = import.meta.env.VITE_MATCH_RESULTS_LOTTERY_NOTICE_UNTIL
const showTempLotteryNotice =
  !!noticeUntil && new Date() < new Date(noticeUntil)

import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  LinearProgress,
  List,
  styled,
  Switch,
  tooltipClasses,
  TooltipProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Box from '@mui/material/Box'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WestIcon from '@mui/icons-material/West'
import { ApplicationOpenCloseDates, ScanResponse } from '@/models/Scans'
import MatchResultsListItem from '../components/MatchResultsListItem'
import { useEffect, useState, useMemo } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import Loading from '../components/Loading'

import { getCurrentUser } from '@/utils/auth/UserProvider'
import { getEffectiveOrgUuid } from '@/utils/utils'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import {
  useApplicationsByNeed,
  useApplicationsSummary,
} from '@/utils/hooks/applications'
import { UUID } from 'crypto'
import { Link, useParams } from 'react-router-dom'
import NotFound from './NotFound'
import {
  getScanProgressMessage,
  useScanTask,
} from '@/utils/tasks/scanTask'
import { useAdoptActiveScan } from '@/utils/tasks/useAdoptActiveScan'
import { useProfileRegenGuard } from '@/components/ProfileRegenBlockProvider'
import OppSuggestionDialog from '@/components/OppSuggestionDialog'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { AccessibleTooltip } from '../components/AccessibleTooltip'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import { localisedDate, parseUTCDate } from '@/utils/dateUtils'
import {
  STALE_SCAN_DAYS,
  EDITED_SINCE_SCAN_MESSAGE,
  getLatestScan,
  getScanBlock,
  hasScanAmount,
} from '@/utils/staleScan'
import { ScanBlockedTooltip } from '@/components/ScanBlockedTooltip'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'

const NO_SCAN_RESULTS: ScanResponse[] = []

export default function MatchResults() {
  usePageTitle('Match Results')
  const params = useParams()
  const fundingNeedID = params.fundingNeedId
  const fundingNeedIdNumber = Number(fundingNeedID)
  const focusRef = useFocusManagement()

  const includeRelational = true
  const [scanComplete, setScanComplete] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<string>('')
  const [isStartingScan, setIsStartingScan] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [showFuturePlanningPrompt, setShowFuturePlanningPrompt] =
    useState(false)

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()
  const {
    orgData,
    orgErrors,
    isLoading: orgsLoading,
    isError: orgsError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)

  const organisation = orgData.find((org) => org.uuid === effectiveOrgUuid)

  // Check if user is on free tier
  const isUserFreeTier = organisation ? isFreeTier(organisation) : true
  const isAnnualSubscriber = organisation?.is_annual_subscriber === true
  const { isPaused, isCancelled, canWrite } = usePausedOrg()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [showHighPotential] = useState(true)
  const [showHiddenMatches, setShowHiddenMatches] = useState(false)

  const {
    data: latestScanResults,
    isLoading: scanResultsLoading,
    isFetching: scanResultsFetching,
    isError,
    refetch,
  } = useLatestScanResultsByNeedId(
    token as string,
    fundingNeedIdNumber,
    showHiddenMatches,
  ) as {
    data: ScanResponse[]
    isLoading: boolean
    isFetching: boolean
    isError: boolean
    refetch: () => void
  }
  const {
    data: scanIdsByNeed,
    isLoading: scanIdsLoading,
    isError: scanIdsError,
  } = useScanIdsByNeedId(token as string, [fundingNeedIdNumber])

  // Fetch applications for this need to check which opportunities already have applications
  const { data: applicationsForNeed, isLoading: applicationsLoading } =
    useApplicationsByNeed(
      token as string,
      effectiveOrgUuid,
      fundingNeedIdNumber,
    )

  const { data: applicationSummaryData, isLoading: applicationSummaryLoading } =
    useApplicationsSummary(token as string, effectiveOrgUuid)
  const {
    data: fundingNeeds,
    isLoading: fundingNeedsLoading,
    isError: fundingNeedsError,
  } = useFundingNeeds(token as string, effectiveOrgUuid)

  const currentFundingNeed = useMemo(
    () =>
      fundingNeeds?.find(
        (fundingNeed) => fundingNeed.id === fundingNeedIdNumber,
      ) ?? null,
    [fundingNeeds, fundingNeedIdNumber],
  )

  const { scanDate: latestScanDate, latestScanFoundNothing } = getLatestScan(
    latestScanResults && latestScanResults.length > 0
      ? latestScanResults[0].scan_date
      : null,
    currentFundingNeed?.last_scanned_at,
  )
  // Once a later scan found nothing, the older batch is not a valid set of
  // matches and is not shown. The API already leaves it out; this also covers
  // a cached batch fetched before that scan finished.
  const data = latestScanFoundNothing ? NO_SCAN_RESULTS : latestScanResults
  const fundingNeedUpdatedAt = currentFundingNeed?.updated_at
    ? parseUTCDate(currentFundingNeed.updated_at)
    : null
  const isEditedSinceLastScan = Boolean(
    fundingNeedUpdatedAt &&
      isValid(fundingNeedUpdatedAt) &&
      latestScanDate &&
      isValid(latestScanDate) &&
      fundingNeedUpdatedAt > latestScanDate,
  )
  const currentScanIds =
    scanIdsByNeed.find((scanIds) => scanIds?.fundingNeedId === fundingNeedIdNumber)
      ?.scanIds ?? []

  // lookup map: opportunity_uuid -> application
  const applicationLookup = useMemo(() => {
    const lookup = new Map()
    applicationsForNeed?.forEach((app) => {
      if (app.opportunity_uuid) {
        lookup.set(app.opportunity_uuid, { id: app.id, status: app.status })
      }
    })
    return lookup
  }, [applicationsForNeed])

  // count number of applications which have a need id
  const totalApplicationsForOrg = applicationSummaryData
    ?.filter((summary) => summary.need_id)
    .reduce((sum, summary) => sum + summary.total_applications, 0)

  // org's application limit from the summary payload; -1 means unlimited, default 1
  const maxTotalApplications =
    applicationSummaryData?.[0]?.max_total_applications ?? 1

  const freeTierDraftRestriction = getDraftRestriction(
    isUserFreeTier,
    totalApplicationsForOrg ?? 0,
    maxTotalApplications,
  )

  // A scan started elsewhere (onboarding, another tab, a colleague) is otherwise
  // invisible here, and its results don't exist yet — this page would read that as
  // "no matches".
  // Polls, unlike the list rows: this page is where a user sits and watches one
  // scan, so it is worth one request every 15s to pick up a scan started elsewhere
  // without a reload.
  const { isSettled: activeScanSettled } = useAdoptActiveScan(
    token as string,
    fundingNeedIdNumber,
    { poll: true },
  )

  const guardRegen = useProfileRegenGuard()

  const {
    startScan,
    isLoadingForNeed,
    errorForNeed,
    progressForNeed,
    messageForNeed,
    statusForNeed,
    hasActiveScan,
    isScanStaleForNeed,
    resetScanForNeed,
    refreshScanState,
  } = useScanTask({
    token: token as string,
    onCompleted: (results, taskId, fundingNeedID) => {
      setIsStartingScan(false)
      setScanComplete(true)
    },
    onError: (error, taskId, needId) => {
      setIsStartingScan(false)
      setScanComplete(false)
      // A profile-regen 409 shows the shared "updating your profile" dialog and
      // must take precedence over the stale-scan notice — handle it first.
      if (guardRegen(error)) return
      // Check if this is a payment required error
      if (error.name === 'PaymentRequired') {
        setUpgradeReason('Running additional scans')
        setShowUpgradeDialog(true)
      }
    },
  })

  // Use useEffect to refresh scan state after navigation
  useEffect(() => {
    // Force refresh scan state when component mounts
    refreshScanState()
  }, [])

  // Get UI state from the task hook
  const needId = fundingNeedID ? String(fundingNeedID) : ''

  // Check if scan is stale
  const isStale = needId ? isScanStaleForNeed(needId) : false

  // Is actively scanning (not stale)
  const isScanning = needId
    ? (hasActiveScan(needId) || isLoadingForNeed(needId) || isStartingScan) &&
      !isStale
    : false

  const scanError = needId ? errorForNeed(needId) : null

  const {
    data: orgScanUsage,
    isLoading: usageLoading,
    isError: usageError,
  } = useOrgScanUsage(token as string, effectiveOrgUuid)
  // A pending or failed usage query must not read as "0 scans used" — see
  // hasFreeUserReachedScanLimit for why unknown usage counts as at cap.
  const totalScansUsed =
    usageLoading || usageError ? undefined : orgScanUsage?.total_scans_used
  const hasReachedScanLimit = hasFreeUserReachedScanLimit(
    totalScansUsed,
    organisation?.max_total_scans,
    isUserFreeTier,
  )
  const canScan = canWrite && !hasReachedScanLimit

  const latestScanDaysOld =
    latestScanDate && isValid(latestScanDate)
      ? differenceInDays(new Date(), latestScanDate)
      : 0
  const isScanResultsStale = latestScanDaysOld >= STALE_SCAN_DAYS
  const isResultsStale =
    !currentFundingNeed?.is_archived &&
    (isScanResultsStale || isEditedSinceLastScan)
  const hasExistingResults = Boolean(data && data.length > 0)
  const shouldForceRescan = isResultsStale && hasExistingResults && canScan
  // The free-tier cap is not a block here: the button stays live so the 402
  // opens the upgrade dialog.
  const scanBlockedReason = currentFundingNeed
    ? (getScanBlock({
        canWrite,
        isPaused,
        isCancelled,
        isArchived: currentFundingNeed.is_archived,
        hasAmount: hasScanAmount(currentFundingNeed),
      })?.message ?? null)
    : null

  // Alanna's product call on #1361 (2026-08-05): while a re-scan is running the
  // user sees progress only, never the previous batch. If you edit a request to
  // fix bad matches and then still see those matches, you conclude the edit did
  // not save — a banner or read-only rendering does not fix that, because the
  // problem is that the old results are misleading, not that they are clickable.
  // This deliberately reverses #2006, which kept the previous batch on screen
  // during a re-scan; waiting is preferable to showing known-wrong matches.
  const hideExistingResults = isScanning || shouldForceRescan

  // SCAN PROGRESS
  const scanProgress = needId
    ? {
        progress: progressForNeed(needId) || 0,
        message: getScanProgressMessage({
          taskMessage: messageForNeed(needId),
          isStale,
          status: statusForNeed(needId),
          isStarting: isStartingScan,
        }),
      }
    : { progress: 0, message: '' }

  // Refetch data when showHiddenMatches changes
  useEffect(() => {
    refetch()
  }, [showHiddenMatches])


  // Compute all opportunities
  const opportunities = useMemo(() => {
    // Stale results (30+ days old, or the request was edited since the last
    // scan) are hidden entirely rather than shown alongside a warning — a
    // re-scan is required first, unless the org can't currently scan. The block
    // holds for the whole re-scan, not just up until it starts.
    if (!data || !data.length || hideExistingResults) {
      return {
        currentOpportunities: [],
        fullCurrentOpportunities: [],
        upcomingOpportunities: [],
        fullUpcomingOpportunities: [],
        rescheduledOpportunities: [],
        futurePlanningOpportunities: [],
        closedBeforeScanIds: new Set<number>(),
        relationalOpportunities: [],
        fullRelationalOpportunities: [],
      }
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    const localToday = new Date(
      today.toLocaleString('en-US', { timeZone: userTimeZone }),
    )
    const upcoming3moCutoff = new Date(localToday)
    upcoming3moCutoff.setMonth(localToday.getMonth() + 3)
    const upcoming12moCutoff = new Date(localToday)
    upcoming12moCutoff.setMonth(localToday.getMonth() + 12)

    // Normalize date to user's local timezone
    const normaliseToLocal = (date: string | Date | null, timeZone: string) => {
      if (!date) return null
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return new Date(dateObj.toLocaleString('en-US', { timeZone }))
    }

    // Checks if opportunity is currently open
    const isCurrent = (dates: ApplicationOpenCloseDates[]) => {
      if (!dates || dates.length === 0) return false

      return dates.some(({ application_open, application_close }) => {
        const normalizedOpen = application_open
          ? normaliseToLocal(application_open, userTimeZone)
          : localToday // Treat null as already open

        const normalizedClose = normaliseToLocal(
          application_close,
          userTimeZone,
        )

        return (
          normalizedOpen &&
          normalizedOpen <= localToday &&
          (!normalizedClose || normalizedClose >= localToday)
        )
      })
    }

    const isUpcomingWithin3mo = (dates: ApplicationOpenCloseDates[]) => {
      if (!dates || dates.length === 0) return false

      return dates.some(({ application_open, application_close }) => {
        const normalizedOpen = application_open
          ? normaliseToLocal(application_open, userTimeZone)
          : null

        const normalizedClose = normaliseToLocal(
          application_close,
          userTimeZone,
        )

        return (
          normalizedOpen &&
          normalizedOpen > localToday &&
          normalizedOpen <= upcoming3moCutoff &&
          (!normalizedClose || normalizedClose >= normalizedOpen)
        )
      })
    }

    const isUpcoming3to12mo = (dates: ApplicationOpenCloseDates[]) => {
      if (!dates || dates.length === 0) return false

      return dates.some(({ application_open, application_close }) => {
        const normalizedOpen = application_open
          ? normaliseToLocal(application_open, userTimeZone)
          : null

        const normalizedClose = normaliseToLocal(
          application_close,
          userTimeZone,
        )

        return (
          normalizedOpen &&
          normalizedOpen > upcoming3moCutoff &&
          normalizedOpen <= upcoming12moCutoff &&
          (!normalizedClose || normalizedClose >= normalizedOpen)
        )
      })
    }

    // Stable sort function with id as tiebreaker to prevent bouncing
    const stableSort = (a: ScanResponse, b: ScanResponse) => {
      // by favourite first
      if (a.favourite && !b.favourite) return -1
      if (!a.favourite && b.favourite) return 1

      // then by admin notes
      if (a.notes && !b.notes) return -1
      if (!a.notes && b.notes) return 1

      // Sort by has_questions next (questions first)
      if (a.opportunity.has_questions && !b.opportunity.has_questions) return -1
      if (!a.opportunity.has_questions && b.opportunity.has_questions) return 1

      // Then by rank (higher first)
      const rankDiff = Number(b.rank) - Number(a.rank)
      if (rankDiff !== 0) return rankDiff

      // Finally by id for stable sorting
      return String(a.id).localeCompare(String(b.id))
    }

    const upcoming = data.filter(
      (r) =>
        isUpcomingWithin3mo(r.application_open_close_dates) &&
        !isCurrent(r.application_open_close_dates) &&
        !r.opportunity.always_open &&
        !r.opportunity.is_relational,
    )

    const upcoming3to12 = isAnnualSubscriber
      ? data.filter(
          (r) =>
            isUpcoming3to12mo(r.application_open_close_dates) &&
            !isUpcomingWithin3mo(r.application_open_close_dates) &&
            !isCurrent(r.application_open_close_dates) &&
            !r.opportunity.always_open,
        )
      : []

    const isClosed = (dates: ApplicationOpenCloseDates[]) =>
      dates &&
      dates.length > 0 &&
      !isCurrent(dates) &&
      !isUpcomingWithin3mo(dates) &&
      !isUpcoming3to12mo(dates)

    // Strips time from scan_date so date-only open/close dates compare correctly
    const getScanRef = (scanDate: Date) => {
      const d = new Date(scanDate)
      d.setHours(0, 0, 0, 0)
      return normaliseToLocal(d, userTimeZone)!
    }

    // Was the opportunity open at scan time?
    const wasActiveAtScanDate = (
      dates: ApplicationOpenCloseDates[],
      scanDate: Date,
    ) => {
      if (!dates || dates.length === 0) return false
      const scanRef = getScanRef(scanDate)
      return dates.some(({ application_open, application_close }) => {
        const normalizedOpen = application_open
          ? normaliseToLocal(application_open, userTimeZone)
          : scanRef // treat null open as already open
        const normalizedClose = normaliseToLocal(
          application_close,
          userTimeZone,
        )
        return (
          normalizedOpen &&
          normalizedOpen <= scanRef &&
          (!normalizedClose || normalizedClose >= scanRef)
        )
      })
    }

    // Was the opportunity upcoming within 3 months at scan time?
    const wasUpcomingWithin3moAtScanDate = (
      dates: ApplicationOpenCloseDates[],
      scanDate: Date,
    ) => {
      if (!dates || dates.length === 0) return false
      const scanRef = getScanRef(scanDate)
      const scan3moCutoff = new Date(scanRef)
      scan3moCutoff.setMonth(scan3moCutoff.getMonth() + 3)
      return dates.some(({ application_open, application_close }) => {
        const normalizedOpen = application_open
          ? normaliseToLocal(application_open, userTimeZone)
          : null
        const normalizedClose = normaliseToLocal(
          application_close,
          userTimeZone,
        )
        return (
          normalizedOpen &&
          normalizedOpen > scanRef &&
          normalizedOpen <= scan3moCutoff &&
          (!normalizedClose || normalizedClose >= normalizedOpen)
        )
      })
    }

    // Was the opportunity open OR upcoming within 3 months at scan time?
    // These results stay visible for all users when the round later closes.
    // Items that were in the 3-12mo window at scan time are NOT included here —
    // they fall naturally into Future Planning (annual only).
    const wasVisibleAtScanDate = (
      dates: ApplicationOpenCloseDates[],
      scanDate: Date,
    ) =>
      wasActiveAtScanDate(dates, scanDate) ||
      wasUpcomingWithin3moAtScanDate(dates, scanDate)

    const current = data.filter(
      (r) =>
        !r.opportunity.is_relational &&
        (isCurrent(r.application_open_close_dates) ||
          r.opportunity.always_open ||
          // Round has since closed — keep visible if it was open/upcoming (<3mo) at scan time
          (isClosed(r.application_open_close_dates) &&
            wasVisibleAtScanDate(
              r.application_open_close_dates,
              new Date(r.scan_date),
            ))),
    )

    // Non-annual only: round was open/upcoming at scan time but funding rounds have updated it 3-12mo out. Annual users see these in Future Planning instead.
    const rescheduled = !isAnnualSubscriber
      ? data.filter(
          (r) =>
            !r.opportunity.is_relational &&
            !r.opportunity.always_open &&
            isUpcoming3to12mo(r.application_open_close_dates) &&
            wasVisibleAtScanDate(
              r.application_open_close_dates,
              new Date(r.scan_date),
            ),
        )
      : []

    // Future planning (closed bucket): rounds that were already past at scan time,
    // OR were in the annual 3-12mo extended window at scan time.
    // Annual subscribers only — non-annual users never see these in their results.
    const closedBeforeScan = isAnnualSubscriber
      ? data.filter(
          (r) =>
            !r.opportunity.always_open &&
            isClosed(r.application_open_close_dates) &&
            !wasVisibleAtScanDate(
              r.application_open_close_dates,
              new Date(r.scan_date),
            ),
        )
      : []

    // Relational opps that belong in future planning are excluded here
    const relational = data.filter(
      (r) =>
        r.opportunity.is_relational &&
        !upcoming3to12.includes(r) &&
        !closedBeforeScan.includes(r),
    )

    // Apply high potential filter and sort consistently
    const filteredRelational = showHighPotential
      ? relational.filter((r) => Number(r.rank) > 3).sort(stableSort)
      : relational.sort(stableSort)

    const filteredUpcoming = showHighPotential
      ? upcoming.filter((r) => Number(r.rank) > 3).sort(stableSort)
      : upcoming.sort(stableSort)

    const filteredClosedBeforeScan = (
      showHighPotential
        ? closedBeforeScan.filter((r) => Number(r.rank) > 3)
        : closedBeforeScan
    ).sort(stableSort)

    const filteredUpcoming3to12 = (
      showHighPotential
        ? upcoming3to12.filter((r) => Number(r.rank) > 3)
        : upcoming3to12
    ).sort(stableSort)

    // Future planning order: opening soon (non-relational) → relational → rounds not confirmed (non-relational)
    const filteredFuturePlanning = [
      ...filteredUpcoming3to12.filter((r) => !r.opportunity.is_relational),
      ...filteredUpcoming3to12.filter((r) => r.opportunity.is_relational),
      ...filteredClosedBeforeScan.filter((r) => r.opportunity.is_relational),
      ...filteredClosedBeforeScan.filter((r) => !r.opportunity.is_relational),
    ]

    const closedBeforeScanIds = new Set(
      filteredClosedBeforeScan.map((r) => r.id),
    )

    const filteredRescheduled = showHighPotential
      ? rescheduled.filter((r) => Number(r.rank) > 3).sort(stableSort)
      : rescheduled.sort(stableSort)

    const filteredCurrent = showHighPotential
      ? current.filter((r) => Number(r.rank) > 3).sort(stableSort)
      : current.sort(stableSort)

    // Apply limits for free tier users
    const limitedRelational = limitResultsForFreeTier(
      filteredRelational,
      isUserFreeTier,
      5,
    )
    const limitedCurrent = limitResultsForFreeTier(
      filteredCurrent,
      isUserFreeTier,
      5,
    )
    const limitedUpcoming = isUserFreeTier ? [] : filteredUpcoming

    return {
      currentOpportunities: limitedCurrent,
      fullCurrentOpportunities: filteredCurrent,
      upcomingOpportunities: limitedUpcoming,
      fullUpcomingOpportunities: filteredUpcoming,
      rescheduledOpportunities: filteredRescheduled,
      futurePlanningOpportunities: filteredFuturePlanning,
      closedBeforeScanIds,
      relationalOpportunities: limitedRelational,
      fullRelationalOpportunities: filteredRelational,
    }
  }, [
    data,
    showHighPotential,
    isUserFreeTier,
    isAnnualSubscriber,
    hideExistingResults,
  ])

  // Destructure for easier access
  const {
    currentOpportunities,
    fullCurrentOpportunities,
    upcomingOpportunities,
    fullUpcomingOpportunities,
    rescheduledOpportunities,
    futurePlanningOpportunities,
    closedBeforeScanIds,
    relationalOpportunities,
    fullRelationalOpportunities,
  } = opportunities

  const totalHighPotential =
    fullCurrentOpportunities.length +
    fullUpcomingOpportunities.length +
    futurePlanningOpportunities.length +
    fullRelationalOpportunities.length

  const RelationalOppsTooltip = styled(
    ({ className, ...props }: TooltipProps) => (
      <AccessibleTooltip {...props} classes={{ popper: className }} />
    ),
  )(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 600,
      border: '1px solid #dadde9',
    },
  }))

  if (fundingNeedID === undefined || isNaN(Number(fundingNeedID))) {
    return <NotFound />
  }

  // The funding-needs list mounts this same query (same cache key), so arriving
  // from it serves a cached empty array and isLoading is already false. Waiting on
  // isFetching too keeps the page from asserting "No matches found" against results
  // that are still on the wire, and on activeScanSettled so an empty list isn't
  // read as "no matches" before we know whether a scan is still producing them.
  const hasResults = !!data?.length
  const awaitingScanResults =
    scanResultsLoading ||
    (scanResultsFetching && !hasResults) ||
    (!hasResults && !activeScanSettled)

  // Any running scan strips the page back to progress alone — see the
  // hideExistingResults comment above for Alanna's #1361 call (2026-08-05) and
  // why it replaces #2006's "keep the previous batch on screen".
  const showProgressOnly = isScanning

  if (
    userLoading ||
    orgsLoading ||
    awaitingScanResults ||
    applicationsLoading ||
    applicationSummaryLoading ||
    fundingNeedsLoading ||
    scanIdsLoading
  ) {
    return <Loading />
  }

  if (
    isError ||
    orgsError ||
    orgErrors.length > 0 ||
    scanIdsError ||
    fundingNeedsError ||
    !currentFundingNeed
  ) {
    return <ErrorMessage />
  }
  async function handleRunScan(includeFuturePlanning = false) {
    setShowFuturePlanningPrompt(false)
    if (!token || !needId) return

    try {
      setIsStartingScan(true)

      // If we have a stale scan, reset it first
      if (isStale) {
        resetScanForNeed(needId)
      }

      // start scan
      const jobId = await startScan(
        needId,
        includeRelational,
        includeFuturePlanning,
      )

      if (!jobId) {
        // scan already in progress or failed to start
        setIsStartingScan(false)
      }
    } catch (error) {
      setIsStartingScan(false)
    }
  }

  function requestRunScan(includeFuturePlanning = false) {
    setShowFuturePlanningPrompt(false)
    // Every scan trigger is disabled with this reason; this keeps any other
    // path from starting a scan the backend will refuse.
    if (scanBlockedReason) return

    handleRunScan(includeFuturePlanning)
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        {!user || !token || (!organisation && <ErrorMessage />)}

        {/* Upgrade Dialog Modal */}
        <UpgradeCTA
          variant="dialog"
          message="Upgrade to our paid plan to unlock unlimited scans and matches. The free plan is limited to 2 scans total and 5 matches per category."
          restrictedFeature={upgradeReason}
          isOpen={showUpgradeDialog}
          onClose={() => setShowUpgradeDialog(false)}
        />

        <Box
          sx={{ pt: 2, pb: { xs: 14, md: 7 }, width: '90%', margin: '0 auto' }}
        >
          <Button
            disableRipple
            sx={{
              mb: 5,
              textTransform: 'none',
              fontSize: '18px',
              color: '#1C3045',
              width: isMobile ? '55vw' : '250px',
              justifyContent: isMobile ? 'center' : 'flex-start',
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
          >
            <Link
              to="/funding-requests"
              style={{
                textDecoration: 'none',
                color: '#1C3045',
                width: '100%',
              }}
            >
              <Typography
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '18px',
                }}
              >
                <WestIcon
                  sx={{
                    mr: isMobile ? '5%' : '10%',
                    fontSize: '18px',
                  }}
                />
                Back to List View
              </Typography>
            </Link>
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h4"
              component="h1"
              ref={focusRef}
              tabIndex={-1}
              sx={{
                '&:focus': {
                  outline: 'none',
                },
              }}
            >
              Match Results for{' '}
              {/* The scan payload carries the same fn.title, but it isn't there
                  before a scan has written results. */}
              {currentFundingNeed?.title ?? 'Funding Request'}
            </Typography>
            {currentFundingNeed?.is_archived && (
              <Chip
                label="Archived"
                sx={{
                  ml: 2,
                  height: '20px',
                  fontSize: '11px',
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                  fontWeight: '500',
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',

                ml: isMobile ? 0 : 3,
                mb: isMobile ? 2 : 0,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: '500',
                  textAlign: isMobile ? 'center' : 'left',
                  fontSize: isMobile ? '20px' : '18px',
                  color: '#1C3045',
                  mt: 2.5,
                }}
              >
                {isScanning ? (
                  'Finding your matches'
                ) : shouldForceRescan ? (
                  'Re-scan to see your matches'
                ) : data && data.length > 0 ? (
                  <>
                    {totalHighPotential} High Potential{' '}
                    {totalHighPotential === 1 ? 'Match' : 'Matches'}
                  </>
                ) : (
                  'No matches found'
                )}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#1C3045',

                  textAlign: isMobile ? 'center' : 'left',
                  fontSize: '16px',
                }}
              >
                {isScanning
                  ? 'This can take a few minutes. Results appear here as soon as they are ready.'
                  : data && data.length > 0
                    ? `Scan Date: ${localisedDate(new Date(data[0].scan_date), 'dd/MM/yyyy hh:mm a')}`
                    : latestScanFoundNothing && currentFundingNeed?.last_scanned_at
                      ? // Same input shape as the scan_date label above: localisedDate
                        // shifts a naive UTC value to local time itself.
                        `Scan Date: ${localisedDate(new Date(currentFundingNeed.last_scanned_at), 'dd/MM/yyyy hh:mm a')}`
                      : 'No scan data available'}
              </Typography>

              {/* <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  ml: isMobile ? 0 : -2,
                  justifyContent: isMobile ? 'center' : 'flex-start',
                }}
              > */}
              {/* <FormControlLabel
              control={
                <Switch
                  defaultChecked
                  onChange={(e) => setShowHighPotential(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#1C3045' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#1C3045',
                    },
                  }}
                />
              }
              label="Show Only High Ranking Results"
              labelPlacement="start"
            /> */}
              {/* </Box> */}
              {!showProgressOnly && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#1C3045',
                    textAlign: isMobile ? 'center' : 'left',
                    mt: 2,
                    ml: isMobile ? 0 : -2,
                    fontStyle: 'italic',
                  }}
                >
                  Is there a funding opportunity you know of that we've missed?{' '}
                  <Link
                    to="#"
                    style={{
                      textDecoration: 'underline',
                      color: '#1C3045',
                    }}
                    onClick={(e) => {
                      e.preventDefault()
                      setDialogOpen(true)
                    }}
                  >
                    Click here
                  </Link>{' '}
                  to let us know.
                </Typography>
              )}
            </Box>

            {dialogOpen && (
              <OppSuggestionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fundingNeedTitle={currentFundingNeed?.title ?? ''}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: 0.5,
                mr: isMobile ? 0 : 2,
              }}
            >
              {isScanning ? (
                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#1C3045',
                      justifyContent: 'flex-end',
                      width: '100%',
                      mt: 1,
                      mb: 0.25,
                      pr: 1.5,
                    }}
                  >
                    <CircularProgress
                      size={18}
                      sx={{ color: '#1C3045', mr: 1.5 }}
                    />
                    {scanProgress.message || 'Matching...'}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={scanProgress.progress || 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mx: 2,
                      bgcolor: '#E0E0E0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#1C3045',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      pr: 2,
                      mt: 0.5,
                      color: '#666',
                    }}
                  >
                    {scanProgress.progress || 0}% complete
                  </Typography>
                </Box>
              ) : scanError ? (
                <Box sx={{ width: '100%' }}>
                  <Typography
                    sx={{
                      fontSize: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#D32F2F',
                      justifyContent: 'flex-end',
                      width: '100%',
                      mt: 1,
                      mb: 0.25,
                      pr: 1.5,
                    }}
                  >
                    Error Finding Matches
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      pr: 2,
                      color: '#D32F2F',
                    }}
                  >
                    Sorry, something went wrong. Please try again later
                  </Typography>
                </Box>
              ) : scanComplete ? (
                <Typography
                  sx={{
                    fontSize: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#1C3045',
                    justifyContent: 'flex-end',
                    width: '100%',
                    mt: 1,
                    mb: 0.25,
                    pr: 1.5,
                  }}
                >
                  <TaskAltIcon
                    sx={{ color: '#1C3045', mr: 1, fontSize: '22px' }}
                  />
                  Scan Complete
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                  }}
                >
                  {scanBlockedReason ? (
                    <AccessibleTooltip
                      title={scanBlockedReason}
                      arrow
                      placement="bottom"
                    >
                      <span>
                        <Button
                          variant="contained"
                          disableRipple
                          disabled={true}
                          sx={{
                            mx: 0.5,
                            fontSize: '16px',
                            textTransform: 'none',
                            color: 'white',
                            bgcolor: '#1C3045',
                            boxShadow: 'none',
                            borderRadius: 2,
                            width: '170px',
                            height: '40px',
                          }}
                        >
                          Re-find Matches
                        </Button>
                      </span>
                    </AccessibleTooltip>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Button
                        variant="contained"
                        disableRipple
                        disabled={isScanning}
                        sx={{
                          mx: 0.5,
                          fontSize: '16px',
                          textTransform: 'none',
                          color: 'white',
                          bgcolor: '#1C3045',
                          boxShadow: 'none',
                          borderRadius: 2,
                          width: '170px',
                          height: '40px',
                        }}
                        onClick={() => {
                          if (
                            isAnnualSubscriber &&
                            futurePlanningOpportunities.length > 0
                          ) {
                            setShowFuturePlanningPrompt(true)
                          } else {
                            requestRunScan(false)
                          }
                        }}
                      >
                        Re-find Matches
                      </Button>
                      {showFuturePlanningPrompt && (
                        <>
                          <Box
                            sx={{ position: 'fixed', inset: 0, zIndex: 9 }}
                            onClick={() => setShowFuturePlanningPrompt(false)}
                          />
                          <Box
                            sx={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              mt: 0.5,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                              bgcolor: '#f5f9ff',
                              borderColor: '#e5f6fd',
                              border: '1px solid',
                              borderRadius: 1,
                              p: 1.25,
                              zIndex: 10,
                              width: isMobile ? 220 : 390,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.25,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#1C3045',
                                  }}
                                >
                                  Include future planning?
                                </Typography>
                                <AccessibleTooltip
                                  title="Includes opportunities opening in the next 3–12 months and those we expect to reopen soon."
                                  placement="top"
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: '#ffffff',
                                        color: '#333333',
                                        boxShadow:
                                          '0 2px 8px rgba(0, 0, 0, 0.15)',
                                        fontSize: '12px',
                                        border: '1px solid #e0e0e0',
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: '#ffffff',
                                        '&::before': {
                                          border: '1px solid #e0e0e0',
                                        },
                                      },
                                    },
                                  }}
                                >
                                  <span
                                    role="img"
                                    aria-label="Info: Includes opportunities opening in the next 3–12 months and those we expect to reopen soon."
                                    tabIndex={0}
                                  >
                                    <HelpOutlineIcon
                                      sx={{
                                        fontSize: '15px',
                                        color: '#1C3045',
                                        cursor: 'help',
                                      }}
                                    />
                                  </span>
                                </AccessibleTooltip>
                              </Box>
                              <Typography
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '12px',
                                }}
                              >
                                Please note: If you include future planning,
                                finding matches will take longer due to more
                                opportunities being available to match with.
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 0.75,
                                mt: 1.5,
                              }}
                            >
                              <Button
                                variant="text"
                                disableRipple
                                size="small"
                                onClick={() =>
                                  setShowFuturePlanningPrompt(false)
                                }
                                sx={{
                                  fontSize: '13px',
                                  textTransform: 'none',
                                  minWidth: 0,
                                  px: 0.5,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    opacity: 0.7,
                                  },
                                }}
                              >
                                Cancel
                              </Button>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                <Button
                                  variant="contained"
                                  disableRipple
                                  size="small"
                                  onClick={() => requestRunScan(true)}
                                  sx={{
                                    fontSize: '13px',
                                    textTransform: 'none',
                                    color: 'white',
                                    bgcolor: '#1C3045',
                                    boxShadow: 'none',
                                    minWidth: 0,
                                    px: 1.5,
                                    '&:hover': { backgroundColor: '#162433' },
                                  }}
                                >
                                  Yes
                                </Button>
                                <Button
                                  variant="outlined"
                                  disableRipple
                                  size="small"
                                  onClick={() => requestRunScan(false)}
                                  sx={{
                                    fontSize: '13px',
                                    textTransform: 'none',
                                    color: '#1C3045',
                                    borderColor: '#1C3045',
                                    minWidth: 0,
                                    px: 1.5,
                                    '&:hover': {
                                      backgroundColor: 'rgba(28,48,69,0.08)',
                                    },
                                  }}
                                >
                                  No
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              )}
              {!showProgressOnly && (
                <FormControlLabel
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: '#1C3045',
                    },
                  }}
                  control={
                    <Switch
                      checked={showHiddenMatches}
                      onChange={(e) => setShowHiddenMatches(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#1C3045',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                          {
                            backgroundColor: '#1C3045',
                          },
                      }}
                    />
                  }
                  label="Show Previously Hidden Matches"
                  labelPlacement="start"
                />
              )}
            </Box>
          </Box>
          {data && data.length > 0 && shouldForceRescan && !isScanning && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 2,
                p: 4,
                mt: 2,
                bgcolor: '#f7f7f7',
                border: 1,
                borderColor: '#1C3045',
                borderRadius: 3,
              }}
            >
              <Typography variant="h6">
                Your matches are out of date
              </Typography>
              <Typography variant="body1" sx={{ maxWidth: 480 }}>
                {isEditedSinceLastScan
                  ? EDITED_SINCE_SCAN_MESSAGE
                  : `Your last scan was ${latestScanDaysOld} days ago. Re-scan to see current matches.`}
              </Typography>
              <ScanBlockedTooltip reason={scanBlockedReason}>
                <Button
                  variant="contained"
                  disableRipple
                  disabled={isScanning || !!scanBlockedReason}
                  onClick={() => requestRunScan(false)}
                  sx={{
                    textTransform: 'none',
                    color: 'white',
                    bgcolor: '#1C3045',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#162433' },
                  }}
                >
                  Re-find Matches
                </Button>
              </ScanBlockedTooltip>
            </Box>
          )}

          {data && data.length > 0 && isResultsStale && !canScan && !isScanning && (
            <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
              {isEditedSinceLastScan
                ? EDITED_SINCE_SCAN_MESSAGE
                : `Your last scan was ${latestScanDaysOld} days ago.`}{' '}
              These results may be out of date.
            </Alert>
          )}

          {data &&
            data.length > 0 &&
            totalHighPotential < 5 &&
            totalHighPotential > 0 && (
              <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
                Oops! Looks like you haven't got many matches. <br /> We can
                help with that! Let us know if you'd like a chat, or some help
                improving your funding request. Email one of our co-founders{' '}
                <Link
                  to={`mailto:${accountContactEmail}`}
                  style={{ color: 'inherit' }}
                >
                  {accountContactEmail}
                </Link>
              </Alert>
            )}

          {!shouldForceRescan &&
            currentOpportunities.length === 0 &&
            upcomingOpportunities.length === 0 &&
            relationalOpportunities.length === 0 &&
            !isScanning && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  width: '50%',
                  margin: '0 auto',
                  mt: 7,
                  bgcolor: '#f7f7f7',
                  borderRadius: 3,
                  border: 1,
                  borderColor: '#1C3045',
                  px: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mt: 4,
                    textAlign: 'center',
                    fontSize: '18px',
                  }}
                >
                  No results? No worries!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    mb: 5,
                    textAlign: 'center',
                    mx: isMobile ? 0 : 10,
                  }}
                >
                  Click the button below to send us a help request and we'll get
                  in touch to give you a hand.
                </Typography>
                <Link to="/help" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="contained"
                    disableRipple
                    sx={{
                      mx: 0.5,
                      fontSize: '16px',
                      textTransform: 'none',
                      color: 'white',
                      bgcolor: '#1C3045',
                      boxShadow: 'none',
                      borderRadius: 2,
                      width: '170px',
                      height: '40px',
                      mt: 1,
                    }}
                  >
                    Get Help
                  </Button>
                </Link>
                <Typography
                  variant="body2"
                  sx={{ mt: 2, mb: 6, textAlign: 'center' }}
                >
                  {' '}
                  Alternatively, you can send us an email directly at{' '}
                  <span style={{ fontWeight: '500' }}>
                    help@fundsorter.com
                  </span>{' '}
                </Typography>
              </Box>
            )}
          {showTempLotteryNotice && (
            <Alert
              severity="info"
              role="status"
              sx={{
                mt: 2,
                width: '100%',
              }}
            >
              <AlertTitle sx={{ fontSize: '14px' }}>
                Important note for Lottery Funds
              </AlertTitle>
              <Typography variant="body2">
                DIA have made changes to the structure of the Lottery Funds. The
                new funds will open on 22 July 2026.
              </Typography>
              <Typography variant="body2">
                Check back here to Re-find Matches from 22 July 2026 to see if
                your request will match with this fund.
              </Typography>
            </Alert>
          )}
          {currentOpportunities.length > 0 && (
            <>
              <Typography variant="h5" component={'h2'} sx={{ mt: 3 }}>
                Open now
              </Typography>
              <List>
                <Divider />
                {currentOpportunities.length > 0 ? (
                  <>
                    {currentOpportunities.map((result) => (
                      <Box key={result.id}>
                        <MatchResultsListItem
                          result={result}
                          existingApplication={applicationLookup.get(
                            result.opportunity_uuid,
                          )}
                          applicationsLoading={applicationsLoading}
                          draftRestriction={freeTierDraftRestriction}
                        />
                      </Box>
                    ))}
                    {/* Upgrade banner for free tier users if there are more matches */}
                    {isUserFreeTier &&
                      fullCurrentOpportunities.length >
                        currentOpportunities.length && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <UpgradeCTA
                            variant="inline"
                            message={`Upgrade to see the remaining ${
                              fullCurrentOpportunities.length -
                              currentOpportunities.length
                            } matches in this category.`}
                          />
                        </Box>
                      )}
                  </>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ my: 4, textAlign: 'center' }}
                  >
                    No opportunities found which are currently open.
                  </Typography>
                )}
              </List>
            </>
          )}

          {/* Show upcoming opportunities section for paid users */}
          {upcomingOpportunities.length > 0 && (
            <>
              <Typography variant="h5" component={'h2'} sx={{ mt: 5 }}>
                Open in the Next 3 Months
              </Typography>
              <List>
                <Divider />
                {upcomingOpportunities.length > 0 ? (
                  upcomingOpportunities.map((result) => (
                    <Box key={result.id}>
                      <MatchResultsListItem
                        result={result}
                        existingApplication={applicationLookup.get(
                          result.opportunity_uuid,
                        )}
                        applicationsLoading={applicationsLoading}
                        draftRestriction={freeTierDraftRestriction}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ my: 4, textAlign: 'center' }}
                  >
                    No upcoming opportunities found.
                  </Typography>
                )}
              </List>
            </>
          )}

          {/* Show upgrade banner for upcoming funders if user is on free tier and there are upcoming opportunities */}
          {isUserFreeTier && fullUpcomingOpportunities.length > 0 && (
            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h5" component={'h2'} sx={{ mb: 2 }}>
                Open in the Next 3 Months
              </Typography>
              <UpgradeCTA
                variant="inline"
                message={`Upgrade to see ${fullUpcomingOpportunities.length} upcoming funding opportunities.`}
              />
            </Box>
          )}

          {/* Opening Later — non-annual only, rounds rescheduled 3-12mo out */}
          {rescheduledOpportunities.length > 0 && (
            <>
              <Typography variant="h5" component={'h2'} sx={{ mt: 5 }}>
                Open again soon
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, mb: 1, color: 'text.secondary' }}
              >
                These opportunities have closed since your scan, but a new round
                is expected to open within 3-12 months. Please check back closer
                to the opening date.
              </Typography>
              <List>
                <Divider />
                {rescheduledOpportunities.map((result) => (
                  <Box key={result.id}>
                    <MatchResultsListItem
                      result={result}
                      existingApplication={applicationLookup.get(
                        result.opportunity_uuid,
                      )}
                      applicationsLoading={applicationsLoading}
                      draftRestriction="not_open"
                    />
                  </Box>
                ))}
              </List>
            </>
          )}

          {relationalOpportunities.length > 0 && (
            <>
              <Typography variant="h5" component={'h2'} sx={{ mt: 5 }}>
                Relational Opportunities{' '}
                <RelationalOppsTooltip
                  title={
                    <>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ px: 1, pt: 2, fontStyle: 'italic' }}
                      >
                        What are Relational Opportunities?
                      </Typography>
                      <Typography variant="body2" sx={{ px: 1, py: 2 }}>
                        Relational opportunities are with Funders who don't have
                        an open grants process, but are open to being
                        approached. You are less likely to be successful with
                        Relational Funders, and they often receive a large
                        number of requests. Read through the information
                        carefully, and only approach them if you believe there
                        is a strong strategic alignment.
                      </Typography>
                    </>
                  }
                >
                  <HelpOutlineIcon
                    sx={{
                      fontSize: '20px',
                      color: '#1C3045',
                    }}
                  />
                </RelationalOppsTooltip>
              </Typography>
              <List>
                <Divider />
                {relationalOpportunities.length > 0 && (
                  <>
                    {relationalOpportunities.map((result) => (
                      <Box key={result.id}>
                        <MatchResultsListItem
                          result={result}
                          existingApplication={applicationLookup.get(
                            result.opportunity_uuid,
                          )}
                          applicationsLoading={applicationsLoading}
                          draftRestriction={freeTierDraftRestriction}
                        />
                      </Box>
                    ))}
                    {/* Upgrade banner for free tier users if there are more matches */}
                    {isUserFreeTier &&
                      fullRelationalOpportunities.length >
                        relationalOpportunities.length && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <UpgradeCTA
                            variant="inline"
                            message={`Upgrade to see the remaining ${
                              fullRelationalOpportunities.length -
                              relationalOpportunities.length
                            } matches in this category.`}
                          />
                        </Box>
                      )}
                  </>
                )}
              </List>
            </>
          )}

          {/* Future Planning */}
          {data && data.length > 0 && (
            <>
              <Typography variant="h5" component={'h2'} sx={{ mt: 5 }}>
                Future Planning
              </Typography>
              {!isAnnualSubscriber ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Upgrade to an annual plan to unlock Future Planning — see
                  opportunities opening in the next 3–12 months and those
                  expected to reopen soon.
                </Alert>
              ) : futurePlanningOpportunities.length === 0 ? (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: '14px', mb: 1 }}
                  >
                    Includes opportunities opening in the next 3–12 months and
                    those we expect to reopen soon.
                  </Typography>
                  <Typography
                    sx={{ color: 'text.secondary', fontSize: '13px', mb: 2 }}
                  >
                    Please note: If you include future planning, finding matches
                    will take longer due to more opportunities being available
                    to match with.
                  </Typography>
                  {isScanning ? (
                    <Box sx={{ maxWidth: 300 }}>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#1C3045',
                          mb: 0.25,
                        }}
                      >
                        <CircularProgress
                          size={16}
                          sx={{ color: '#1C3045', mr: 1.5 }}
                        />
                        {scanProgress.message || 'Matching...'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={scanProgress.progress || 0}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#E0E0E0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#1C3045',
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: 'flex', mt: 0.5, color: '#666' }}
                      >
                        {scanProgress.progress || 0}% complete
                      </Typography>
                    </Box>
                  ) : scanError ? (
                    <Typography sx={{ color: '#D32F2F', fontSize: '14px' }}>
                      Sorry, something went wrong. Please try again later.
                    </Typography>
                  ) : (
                    <ScanBlockedTooltip reason={scanBlockedReason}>
                      <Button
                        variant="contained"
                        disableRipple
                        disabled={!!scanBlockedReason}
                        onClick={() => requestRunScan(true)}
                        sx={{
                          fontSize: '14px',
                          textTransform: 'none',
                          color: 'white',
                          bgcolor: '#1C3045',
                          boxShadow: 'none',
                          borderRadius: 2,
                          '&:hover': { backgroundColor: '#162433' },
                        }}
                      >
                        Re-find Matches with future planning
                      </Button>
                    </ScanBlockedTooltip>
                  )}
                </Box>
              ) : (
                <List>
                  <Divider />
                  {futurePlanningOpportunities.map((result) => (
                    <Box key={result.id}>
                      <MatchResultsListItem
                        result={result}
                        isFuturePlanning={closedBeforeScanIds.has(result.id)}
                        existingApplication={applicationLookup.get(
                          result.opportunity_uuid,
                        )}
                        applicationsLoading={applicationsLoading}
                        draftRestriction="not_open"
                      />
                    </Box>
                  ))}
                </List>
              )}
            </>
          )}

          {data &&
            data.length > 0 &&
            currentOpportunities.length +
              upcomingOpportunities.length +
              relationalOpportunities.length >
              2 &&
            !currentFundingNeed?.is_archived && (
              <Button
                disableRipple
                sx={{
                  my: 5,
                  textTransform: 'none',
                  fontSize: '18px',
                  color: '#1C3045',
                  width: isMobile ? '55vw' : '250px',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                <Link
                  to="/funding-requests"
                  style={{
                    textDecoration: 'none',
                    color: '#1C3045',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '18px',
                    }}
                  >
                    <WestIcon
                      sx={{
                        mr: isMobile ? '5%' : '10%',
                        fontSize: '18px',
                      }}
                    />
                    Back to List View
                  </Typography>
                </Link>
              </Button>
            )}
        </Box>
      </Authenticated>
    </>
  )
}
